import Swal from "sweetalert2";
import { LuPercent } from "react-icons/lu";
import Multiselect from "multiselect-react-dropdown";
import { useCallback, useEffect, useState } from "react";
import { FiCopy, FiRotateCcw, FiCheck, FiDollarSign } from "react-icons/fi";
import { Button, Card, Col, Container, Form, InputGroup, Row } from "react-bootstrap";
import { storeCouponServices } from "../../../../../APIServices/SitePilotManagement/StoreManagement/storeCouponServices";

const CHAR_SETS = {
  lowercase: "abcdefghijklmnopqrstuvwxyz",
  uppercase: "ABCDEFGHIJKLMNOPQRSTUVWXYZ",
  numbers: "0123456789",
  special: "!@#$%^&*()_+-=[]{}|;:,.<>?",
};

const CouponData = ({ couponData, getStoreCouponList }) => {
  const [loader, setLoader] = useState(false);
  const [length, setLength] = useState(10);
  const [copied, setCopied] = useState(false);
  const [coupon, setCoupon] = useState("");
  const [amount, setAmount] = useState(0);
  const [discountType, setDiscountType] = useState("flat");
  const [expiry, setExpiry] = useState("");
  const [restrictionType, setRestrictionType] = useState("");
  const [customers, setCustomers] = useState([]);
  const [usageLimits, setUsageLimits] = useState("");

  const [options, setOptions] = useState({
    lowercase: true,
    uppercase: true,
    numbers: true,
    special: false,
  });

  const generateCoupon = useCallback(() => {
    let chars = "";

    Object.keys(options).forEach((key) => {
      if (options[key]) chars += CHAR_SETS[key];
    });

    if (!chars) {
      setCoupon("");
      return;
    }

    let result = "";
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    setCoupon(result);
    setCopied(false);
  }, [length, options]);

  const handleCopy = async () => {
    if (!coupon) return;

    await navigator.clipboard.writeText(coupon);
    setCopied(true);

    setTimeout(() => setCopied(false), 2000);
  };

  const handleChange = (key) => {
    setOptions((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const getStoreSingleCoupon = async () => {
    let data = await storeCouponServices.getStoreSingleCoupon(couponData?.couponId)
    setCoupon(data?.data?.coupon || "")
    setAmount(data?.data?.amount || 0)
    setDiscountType(data?.data?.discountType || "flat")
    setExpiry(data?.data?.expiry || "")
    setRestrictionType(data?.data?.restrictionType || "")
    setCustomers(data?.data?.customers || [])
    setUsageLimits(data?.data?.usageLimits || "")
  }

  const updatedStoreCoupon = async () => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to update settings this Coupon?",
      icon: "warning",
      showCancelButton: true,
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, update it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoader(true)
        let userData = {}
        userData.couponId = couponData?.couponId
        userData.coupon = coupon
        userData.amount = amount
        userData.discountType = discountType
        userData.expiry = expiry
        userData.restrictionType = restrictionType
        userData.customers = customers
        userData.usageLimits = usageLimits
        userData.settings = "settings"
        let data = await storeCouponServices.updatedStoreCoupon(userData)
        if (data?.statusCode === 200) {
          getStoreCouponList()
          getStoreSingleCoupon()
        }
        setLoader(false)
      }
    });
  };

  useEffect(() => {
    if (couponData?.couponId) {
      getStoreSingleCoupon();
    }
  }, [couponData]);

  return (
    <Container fluid className="mt-2">
      <Row className="justify-content-center">
        <Form.Group as={Col} xxl={6} xl={8} lg={10} md={12} sm={12}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-4">
              <div className="mb-4 hstack justify-content-between">
                <h5 className="m-0">Coupon Settings</h5>
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="hstack gap-1 rounded-1 px-3"
                  style={{ fontSize: 12 }}
                  disabled={!coupon || !amount || !discountType || !expiry}
                  onClick={updatedStoreCoupon}
                >
                  Save
                </Button>
              </div>
              <Form.Group className="mb-3">
                <InputGroup>
                  <Form.Control
                    type="text"
                    value={coupon}
                    style={{ width: "45%" }}
                    placeholder="Enter or Generate Coupon"
                    onChange={(e) => setCoupon(e.target.value)}
                  />
                  <Form.Control
                    type="number"
                    min={4}
                    max={20}
                    value={length}
                    onChange={(e) => setLength(Number(e.target.value))}
                  />
                  <InputGroup.Text
                    className="CP"
                    onClick={generateCoupon}
                    title="Regenerate"
                  >
                    <FiRotateCcw />
                  </InputGroup.Text>
                  <InputGroup.Text
                    className={`CP ${copied ? "text-success" : ""}`}
                    onClick={handleCopy}
                    title={copied ? "Copied!" : "Copy"}
                  >
                    {copied ? <FiCheck /> : <FiCopy />}
                  </InputGroup.Text>
                </InputGroup>
              </Form.Group>
              <Row>
                <Col>
                  <Form.Check
                    type="checkbox"
                    label="Lowercase"
                    checked={options.lowercase}
                    onChange={() => handleChange("lowercase")}
                  />
                </Col>
                <Col>
                  <Form.Check
                    type="checkbox"
                    label="Uppercase"
                    checked={options.uppercase}
                    onChange={() => handleChange("uppercase")}
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Check
                    type="checkbox"
                    label="Numbers"
                    checked={options.numbers}
                    onChange={() => handleChange("numbers")}
                  />
                </Col>
                <Col>
                  <Form.Check
                    type="checkbox"
                    label="Special Characters"
                    checked={options.special}
                    onChange={() => handleChange("special")}
                  />
                </Col>
              </Row>
              <Form.Group className="mb-2">
                <Form.Label>
                  Coupon Amount <span className="text-danger">*</span>
                </Form.Label>
                <InputGroup>
                  {discountType === "flat" &&
                    <InputGroup.Text
                      className="CP"
                      title="Flat"
                    >
                      <FiDollarSign />
                    </InputGroup.Text>
                  }
                  <Form.Control
                    type="number"
                    value={amount}
                    placeholder="Enter Coupon Amount"
                    onChange={(e) => setAmount(e.target.value)}
                  />
                  {discountType === "percentage" &&
                    <InputGroup.Text
                      className="CP"
                      title="Percentage"
                    >
                      <LuPercent />
                    </InputGroup.Text>
                  }
                </InputGroup>
              </Form.Group>
              <Form.Group className="my-2">
                <Form.Label>
                  Discount Type <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  className="interactive-input"
                  style={{ fontSize: 12 }}
                  onChange={(e) => setDiscountType(e.target.value)}
                  value={discountType}
                >
                  <option value="">Select Discount Type</option>
                  <option value="flat">Flat</option>
                  <option value="percentage">Percentage (%)</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>
                  Coupon Expiry <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  value={expiry}
                  placeholder="Enter Coupon Expiry"
                  onChange={(e) => setExpiry(e.target.value)}
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>
                  Restriction Type
                </Form.Label>
                <Form.Select
                  className="interactive-input"
                  style={{ fontSize: 12 }}
                  onChange={(e) => setRestrictionType(e.target.value)}
                  value={restrictionType}
                >
                  <option value="">Select Restriction Type</option>
                  <option value="include">Include User</option>
                  <option value="exclude">Exclude User</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label className="mb-1 fw-bold">Customers</Form.Label>
                <Multiselect
                  placeholder="Select Customers"
                  onKeyPressFn={() => { }}
                  onSearch={() => { }}
                  onSelect={(selectedList) =>
                    setCustomers(selectedList)
                  }
                  onRemove={(selectedList) =>
                    setCustomers(selectedList)
                  }
                  selectedValues={customers}
                  options={[
                    { label: 'PDF File', value: '.pdf' },
                    { label: 'MP3 File', value: '.mp3' },
                    { label: 'MP4 File', value: '.mp4' },
                    { label: 'PNG File', value: '.png' },
                    { label: 'JPG File', value: '.jpg' },
                    { label: 'JPEG File', value: '.jpeg' },
                    { label: 'ZIP File', value: '.zip' },
                    { label: 'RAR File', value: '.rar' },
                    { label: 'XPT File', value: '.xpt' },
                    { label: 'CSV File', value: '.csv' },
                    { label: 'XLS File', value: '.xls' },
                    { label: 'XLSX File', value: '.xlsx' },
                    { label: 'DOC File', value: '.doc' },
                    { label: 'DOCX File', value: '.docx' },
                    { label: 'PPT File', value: '.ppt' },
                    { label: 'PPTX File', value: '.pptx' },
                    { label: 'TXT File', value: '.txt' },
                  ]}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>
                  Usage Limits
                </Form.Label>
                <Form.Control
                  type="number"
                  onChange={(e) => setUsageLimits(e.target.value)}
                  value={usageLimits}
                  placeholder="Unlimited"
                />
              </Form.Group>
            </Card.Body>
          </Card>
        </Form.Group>
      </Row>
    </Container>
  );
};

export default CouponData;
